import React, { ChangeEvent } from 'react';
import {
  FieldError,
  FieldValues,
  UseFormMethods,
  RegisterOptions,
} from 'react-hook-form';
import { get } from 'lodash';
import classNames from 'classnames';
import warningImage from '../../../assets/warning.png';
import acceptedImage from '../../../assets/accepted.png';
import arrowDownImage from '../../../assets/arrow-down.png';
import {
  getValidationErrorMessage,
  useTriggerValidationOnMount,
} from '../../../utils/validation';
import { TextFieldProps } from '../TextField/TextField';
// eslint-disable-next-line import/named
import { FlattenKeys } from '../../../types/utilTypes';
import { TooltipIcon } from '../../TooltipIcon/TooltipIcon';
import styles from './styles.module.scss';

type Props<T extends FieldValues> = {
  className?: string;
  label: React.ReactNode;
  tooltip?: string;
  width?: TextFieldProps<T>['width'];
  disabled?: boolean;
  options: {
    label: string;
    value: string | number;
  }[];
  addEmptyOption?: boolean; // adds an empty option to the available list of options
  emptyOptionText?: string;
  onChange?: (val: ChangeEvent<HTMLSelectElement>) => void;
} & (
  | {
      // name is required when form is set
      name: FlattenKeys<T>;
      form: UseFormMethods<T>;
      validation?: RegisterOptions;
      // the form should control the component when it's set
      value?: undefined;
    }
  | {
      name?: string;
      form?: undefined;
      // validation requires a form to be provided
      validation?: undefined;
      value?: string;
    }
);

export const Select = <T extends FieldValues>({
  className,
  label,
  tooltip,
  disabled,
  options: optionsProp,
  addEmptyOption,
  name,
  width,
  form,
  validation,
  emptyOptionText,
  ...restProps
}: Props<T>) => {
  // if there's data in the form when this component is mounted, trigger validation
  useTriggerValidationOnMount(form, name as any);

  const validationError = get(form?.errors, name as string) as
    | FieldError
    | undefined;

  const options = [...optionsProp];
  if (addEmptyOption) {
    options.unshift({ label: emptyOptionText || '', value: '' });
  }

  return (
    <div
      className={classNames(
        styles.container,
        styles[width || 'full'],
        className,
      )}
    >
      <select
        ref={form?.register(validation)}
        className={classNames(
          styles.input,
          !!validationError && styles.invalid,
        )}
        name={name as string}
        disabled={disabled}
        {...restProps}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className={styles.label}>
        {label}
        {tooltip && <TooltipIcon content={tooltip} />}
      </span>
      {/* eslint-disable-next-line no-nested-ternary */}
      {validationError ? (
        <>
          <div className={styles.icon}>
            <img src={warningImage} alt="Warning" />
          </div>
          <span className={styles.warningText}>
            {!!validationError && getValidationErrorMessage(validationError)}
          </span>
        </>
      ) : get(form?.formState.touched, name as string) ? (
        <div className={styles.icon}>
          <img src={acceptedImage} alt="Filled" />
        </div>
      ) : (
        <div className={styles.icon}>
          <img
            className={styles.arrowDownImg}
            src={arrowDownImage}
            alt="Select"
          />
        </div>
      )}
    </div>
  );
};
